import { useCallback, useEffect, useMemo, useState, ChangeEvent } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  colors,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FullScreenDialog from "components/FullScreenDialog";
import ConfirmDialog from "components/ConfirmDialog";
import FormField from "layouts/pages/account/components/FormField";
import { setMessage, useMaterialUIController } from "context";
import { formatMoneyBR } from "helpers/formatMoney";
import { currencyToNumber } from "helpers/utils";
import { formatDateEndOfDay } from "helpers/formatDate";
import {
  IPrecificacaoOrcamento,
  IServicoOrcamento,
  ISolicitacaoOrcamento,
  ORIGEM_SOLICITACAO_LABEL,
  STATUS_SOLICITACAO_LABEL,
  StatusSolicitacaoOrcamento,
} from "interfaces/orcamento";
import { listarServicosOrcamentoAdmin } from "services/orcamentoServicos";
import {
  atualizarItensSolicitacaoOrcamento,
  atualizarSolicitacaoOrcamento,
  atualizarStatusSolicitacaoOrcamento,
  baixarPropostaOrcamentoPdf,
  enviarPropostaOrcamento,
  excluirSolicitacaoOrcamento,
  obterPrecificacaoOrcamento,
  obterSolicitacaoOrcamento,
} from "services/orcamentoSolicitacoes";

type ItemEditavel = IPrecificacaoOrcamento["itens"][number] & {
  lineKey: string;
};

type ItemInputDraft = {
  qtd: string;
  unit: string;
};

function attachLineKeys(
  itens: IPrecificacaoOrcamento["itens"]
): ItemEditavel[] {
  return normalizeItens(
    itens.map((item) => ({
      ...item,
      lineKey: String(item.id),
    }))
  );
}

function createItemFromServico(servico: IServicoOrcamento): ItemEditavel {
  const preco = Math.max(0, Number(servico.precoUnitario) || 0);
  return normalizeItem({
    id: 0,
    lineKey: `new-${servico.id}`,
    servicoId: servico.id,
    nome: servico.nome,
    unidadeMedida: servico.unidadeMedida,
    permiteQuantidade: servico.permiteQuantidade,
    quantidade: 1,
    valorUnitario: preco,
    valorTotal: preco,
    precoUnitarioCatalogo: preco,
    observacao: null,
  });
}

function calcularTotalItem(quantidade: number, valorUnitario: number) {
  return Math.round(quantidade * valorUnitario * 100) / 100;
}

function normalizeItem(item: ItemEditavel): ItemEditavel {
  const quantidade = item.permiteQuantidade
    ? Math.max(1, Math.floor(Number(item.quantidade) || 1))
    : 1;
  const valorUnitario = Math.max(0, Number(item.valorUnitario) || 0);

  return {
    ...item,
    quantidade,
    valorUnitario,
    valorTotal: calcularTotalItem(quantidade, valorUnitario),
  };
}

function normalizeItens(itens: ItemEditavel[]) {
  return itens.map(normalizeItem);
}

function buildInputDrafts(
  itens: ItemEditavel[]
): Record<string, ItemInputDraft> {
  return itens.reduce<Record<string, ItemInputDraft>>((acc, item) => {
    acc[item.lineKey] = {
      qtd: String(item.quantidade),
      unit: valorUnitarioToDigits(item.valorUnitario),
    };
    return acc;
  }, {});
}

function valorUnitarioToDigits(value: number | string): string {
  const num = Math.max(0, Number(value) || 0);
  return String(Math.round(num * 100));
}

function getUnitDisplayValue(
  unitDigits: string | undefined,
  fallback: number
): string {
  return formatMoneyBR(
    currencyToNumber(unitDigits ?? valorUnitarioToDigits(fallback))
  );
}

function getValorPropostoDisplayValue(digits: string): string {
  if (!digits.trim()) return "";
  return formatMoneyBR(currencyToNumber(digits));
}

function parseValorPropostoManual(digits: string): number | undefined {
  if (!digits.trim()) return undefined;
  return currencyToNumber(digits);
}

function parseQuantidadeInput(raw: string): number {
  if (raw.trim() === "") return 1;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

const STATUS_OPTIONS: StatusSolicitacaoOrcamento[] = [
  "NOVO",
  "EM_ANALISE",
  "AGUARDANDO_CLIENTE",
  "PROPOSTA_ENVIADA",
  "APROVADO",
  "RECUSADO",
  "CANCELADO",
];

const STATUS_PROPOSTA_JA_ENVIADA: StatusSolicitacaoOrcamento[] = [
  "PROPOSTA_ENVIADA",
  "APROVADO",
  "RECUSADO",
];

function podeExcluirSolicitacao(solicitacao: ISolicitacaoOrcamento) {
  if (solicitacao.propostaEnviadaEm) return false;
  return !STATUS_PROPOSTA_JA_ENVIADA.includes(solicitacao.status);
}
const STATUS_AUTOCOMPLETE_OPTIONS = STATUS_OPTIONS.map((value) => ({
  value,
  label: STATUS_SOLICITACAO_LABEL[value],
}));

function statusColor(status: StatusSolicitacaoOrcamento) {
  switch (status) {
    case "NOVO":
      return "warning";
    case "EM_ANALISE":
      return "info";
    case "PROPOSTA_ENVIADA":
      return "primary";
    case "APROVADO":
      return "success";
    case "RECUSADO":
    case "CANCELADO":
      return "error";
    default:
      return "default";
  }
}

const sectionCardSx = {
  width: "100%",
  backgroundColor: "#fff",
  borderRadius: "16px",
  border: `1px solid ${colors.grey[300]}`,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  p: { xs: 2, md: 3 },
};

const pageContainerSx = {
  width: "100%",
  maxWidth: 1380,
  mx: "auto",
  px: { xs: 2, sm: 3, md: 4, lg: 5 },
  py: { xs: 2, md: 3 },
  pb: 5,
  display: "flex",
  flexDirection: "column",
  gap: 2.5,
};

interface Props {
  open: boolean;
  solicitacaoId: number | null;
  onClose: () => void;
  onUpdated: () => void;
}

export default function SolicitacaoDetalheDrawer({
  open,
  solicitacaoId,
  onClose,
  onUpdated,
}: Props) {
  const [, dispatch] = useMaterialUIController();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloadingPdf] = useState(false);
  const [solicitacao, setSolicitacao] = useState<ISolicitacaoOrcamento | null>(
    null
  );
  const [itensEdit, setItensEdit] = useState<ItemEditavel[]>([]);
  const [itemInputs, setItemInputs] = useState<Record<string, ItemInputDraft>>(
    {}
  );
  const [catalogoServicos, setCatalogoServicos] = useState<IServicoOrcamento[]>(
    []
  );
  const [servicoParaAdicionar, setServicoParaAdicionar] =
    useState<IServicoOrcamento | null>(null);
  const [obsInterna, setObsInterna] = useState("");
  const [status, setStatus] = useState<StatusSolicitacaoOrcamento>("NOVO");
  const [mensagemEmail, setMensagemEmail] = useState("");
  const [valorPropostoManual, setValorPropostoManual] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const applyItensFromApi = useCallback(
    (itens: IPrecificacaoOrcamento["itens"]) => {
      const normalized = attachLineKeys(itens);
      setItensEdit(normalized);
      setItemInputs(buildInputDrafts(normalized));
    },
    []
  );

  const load = useCallback(async () => {
    if (!solicitacaoId) return;
    setLoading(true);
    try {
      const [detalhe, prec, servicos] = await Promise.all([
        obterSolicitacaoOrcamento(solicitacaoId),
        obterPrecificacaoOrcamento(solicitacaoId),
        listarServicosOrcamentoAdmin(),
      ]);
      setSolicitacao(detalhe);
      setCatalogoServicos(servicos.filter((s) => s.ativo));
      applyItensFromApi(prec.itens);
      setObsInterna(detalhe.obsInterna ?? "");
      setStatus(detalhe.status);
      setValorPropostoManual(
        detalhe.valorProposto != null
          ? valorUnitarioToDigits(Number(detalhe.valorProposto))
          : ""
      );
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error
            ? error.message
            : "Falha ao carregar solicitação.",
      });
    } finally {
      setLoading(false);
    }
  }, [applyItensFromApi, dispatch, solicitacaoId]);

  useEffect(() => {
    if (open && solicitacaoId) {
      load();
    } else {
      setSolicitacao(null);
      setItensEdit([]);
      setItemInputs({});
      setCatalogoServicos([]);
      setServicoParaAdicionar(null);
    }
  }, [open, solicitacaoId, load]);

  const valorCalculado = useMemo(
    () =>
      itensEdit.reduce((acc, item) => acc + (Number(item.valorTotal) || 0), 0),
    [itensEdit]
  );

  const ajusteProposta = useMemo(() => {
    if (!valorPropostoManual.trim() || valorCalculado <= 0) return null;

    const valorEfetivo = parseValorPropostoManual(valorPropostoManual);
    if (valorEfetivo == null) return null;

    const diff = Math.round((valorEfetivo - valorCalculado) * 100) / 100;
    if (Math.abs(diff) < 0.01) return null;

    const percentual = Math.abs((diff / valorCalculado) * 100);

    return {
      tipo: diff < 0 ? ("desconto" as const) : ("acrescimo" as const),
      valor: Math.abs(diff),
      percentual,
    };
  }, [valorPropostoManual, valorCalculado]);

  const servicosDisponiveis = useMemo(() => {
    const usados = new Set(itensEdit.map((item) => item.servicoId));
    return catalogoServicos.filter((servico) => !usados.has(servico.id));
  }, [catalogoServicos, itensEdit]);

  const edicaoBloqueada = solicitacao?.status === "APROVADO";
  const podeExcluir = solicitacao ? podeExcluirSolicitacao(solicitacao) : false;

  const handleExcluir = async () => {
    if (!solicitacaoId || !solicitacao || !podeExcluir) return;
    setDeleting(true);
    try {
      await excluirSolicitacaoOrcamento(solicitacaoId);
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: `Solicitação ${solicitacao.codigo} excluída.`,
      });
      setConfirmDeleteOpen(false);
      onUpdated();
      onClose();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error
            ? error.message
            : "Falha ao excluir solicitação.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleQuantidadeInputChange = (lineKey: string, raw: string) => {
    if (edicaoBloqueada) return;
    setItemInputs((prev) => ({
      ...prev,
      [lineKey]: { ...prev[lineKey], qtd: raw },
    }));

    setItensEdit((prev) =>
      prev.map((item) => {
        if (item.lineKey !== lineKey) return item;
        const quantidade = item.permiteQuantidade
          ? parseQuantidadeInput(raw)
          : 1;
        return {
          ...item,
          quantidade,
          valorTotal: calcularTotalItem(quantidade, item.valorUnitario),
        };
      })
    );
  };

  const handleValorUnitarioInputChange = (lineKey: string, raw: string) => {
    if (edicaoBloqueada) return;
    const digits = raw.replace(/\D/g, "");

    setItemInputs((prev) => ({
      ...prev,
      [lineKey]: { ...prev[lineKey], unit: digits },
    }));

    const valorUnitario = currencyToNumber(digits);
    setItensEdit((prev) =>
      prev.map((item) => {
        if (item.lineKey !== lineKey) return item;
        return {
          ...item,
          valorUnitario,
          valorTotal: calcularTotalItem(item.quantidade, valorUnitario),
        };
      })
    );
  };

  const handleQuantidadeBlur = (lineKey: string) => {
    setItemInputs((prev) => {
      const qtd = parseQuantidadeInput(prev[lineKey]?.qtd ?? "1");
      setItensEdit((items) =>
        items.map((item) => {
          if (item.lineKey !== lineKey) return item;
          const quantidade = item.permiteQuantidade ? qtd : 1;
          return {
            ...item,
            quantidade,
            valorTotal: calcularTotalItem(quantidade, item.valorUnitario),
          };
        })
      );
      return { ...prev, [lineKey]: { ...prev[lineKey], qtd: String(qtd) } };
    });
  };

  const handleValorUnitarioBlur = (lineKey: string) => {
    setItemInputs((prev) => {
      const valorUnitario = currencyToNumber(prev[lineKey]?.unit ?? "0");
      setItensEdit((items) =>
        items.map((item) =>
          item.lineKey === lineKey
            ? {
                ...item,
                valorUnitario,
                valorTotal: calcularTotalItem(item.quantidade, valorUnitario),
              }
            : item
        )
      );
      return {
        ...prev,
        [lineKey]: {
          ...prev[lineKey],
          unit: valorUnitarioToDigits(valorUnitario),
        },
      };
    });
  };

  const handleRemoverItem = (lineKey: string) => {
    if (edicaoBloqueada) return;
    if (itensEdit.length <= 1) {
      setMessage(dispatch, {
        open: true,
        severity: "warning",
        text: "A solicitação deve manter ao menos um serviço.",
      });
      return;
    }

    setItensEdit((prev) => prev.filter((item) => item.lineKey !== lineKey));
    setItemInputs((prev) => {
      const next = { ...prev };
      delete next[lineKey];
      return next;
    });
  };

  const handleAdicionarServico = (servico: IServicoOrcamento | null) => {
    if (edicaoBloqueada) return;
    if (!servico) return;

    if (itensEdit.some((item) => item.servicoId === servico.id)) {
      setMessage(dispatch, {
        open: true,
        severity: "warning",
        text: "Este serviço já está na lista de precificação.",
      });
      return;
    }

    const novoItem = createItemFromServico(servico);
    setItensEdit((prev) => [...prev, novoItem]);
    setItemInputs((prev) => ({
      ...prev,
      [novoItem.lineKey]: {
        qtd: String(novoItem.quantidade),
        unit: valorUnitarioToDigits(novoItem.valorUnitario),
      },
    }));
    setServicoParaAdicionar(null);
  };

  const handleSalvarItens = async (
    atualizarPrecosCatalogo = false,
    silent = false
  ) => {
    if (!solicitacaoId || edicaoBloqueada) return;
    setSaving(true);
    try {
      const prec = await atualizarItensSolicitacaoOrcamento(solicitacaoId, {
        atualizarPrecosCatalogo,
        itens: itensEdit.map((item) => {
          const payload = {
            quantidade: item.quantidade,
            valorUnitario: item.valorUnitario,
          };
          if (item.id > 0) {
            return { id: item.id, ...payload };
          }
          return { servicoId: item.servicoId, ...payload };
        }),
      });
      applyItensFromApi(prec.itens);
      if (!silent) {
        setMessage(dispatch, {
          open: true,
          severity: "success",
          text: atualizarPrecosCatalogo
            ? "Preços atualizados do catálogo."
            : "Itens salvos com sucesso.",
        });
      }
      onUpdated();
      return prec;
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao salvar itens.",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSalvarObsStatus = async () => {
    if (!solicitacaoId || !solicitacao || edicaoBloqueada) return;
    setSaving(true);
    try {
      if (status !== solicitacao.status) {
        await atualizarStatusSolicitacaoOrcamento(solicitacaoId, { status });
      }
      await atualizarSolicitacaoOrcamento(solicitacaoId, {
        obsInterna: obsInterna || null,
      });
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "Dados internos salvos.",
      });
      await load();
      onUpdated();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao salvar.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleValorPropostoInputChange = (raw: string) => {
    if (edicaoBloqueada) return;
    setValorPropostoManual(raw.replace(/\D/g, ""));
  };

  const handleEnviarProposta = async () => {
    if (!solicitacaoId || edicaoBloqueada) return;
    setSending(true);
    try {
      await handleSalvarItens(false, true);
      const valorManual = parseValorPropostoManual(valorPropostoManual);

      const result = await enviarPropostaOrcamento(solicitacaoId, {
        atualizarPrecosCatalogo: false,
        valorProposto: valorManual,
        mensagemEmail: mensagemEmail.trim() || null,
        obsInterna: obsInterna.trim() || null,
      });

      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: result.message || "Proposta enviada por e-mail com PDF.",
      });
      await load();
      onUpdated();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text:
          error instanceof Error ? error.message : "Falha ao enviar proposta.",
      });
    } finally {
      setSending(false);
    }
  };

  const handleBaixarPdf = async () => {
    if (!solicitacaoId || !solicitacao) return;
    setDownloadingPdf(true);
    try {
      await handleSalvarItens(false, true);
      const valorManual = parseValorPropostoManual(valorPropostoManual);

      await baixarPropostaOrcamentoPdf(solicitacaoId, {
        codigo: solicitacao.codigo,
        valorProposto: valorManual,
        mensagem: mensagemEmail.trim() || undefined,
      });

      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: "PDF da proposta baixado com sucesso.",
      });
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: error instanceof Error ? error.message : "Falha ao baixar PDF.",
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const dialogTitle = solicitacao
    ? `Solicitação ${solicitacao.codigo}`
    : "Solicitação de orçamento";

  return (
    <FullScreenDialog open={open} title={dialogTitle} handleClose={onClose}>
      <MDBox
        sx={{
          flex: 1,
          width: "100%",
          overflowY: "auto",
          bgcolor: colors.grey[100],
        }}
      >
        {loading || !solicitacao ? (
          <MDBox sx={pageContainerSx}>
            <Typography color="text.secondary">
              {loading ? "Carregando…" : "Selecione uma solicitação."}
            </Typography>
          </MDBox>
        ) : (
          <MDBox sx={pageContainerSx}>
            <MDBox
              display="flex"
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
              gap={2}
              flexWrap="wrap"
            >
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold">
                  Resumo da solicitação
                </MDTypography>
                <MDTypography variant="body2" color="text.secondary" mt={0.5}>
                  {edicaoBloqueada
                    ? "Esta proposta foi aprovada e está disponível apenas para consulta."
                    : "Revise os dados, ajuste a precificação e envie a proposta ao organizador."}
                </MDTypography>
              </MDBox>
              {podeExcluir ? (
                <MDButton
                  variant="outlined"
                  color="error"
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={saving || sending || downloading || deleting}
                  startIcon={<DeleteOutlineIcon />}
                >
                  Excluir
                </MDButton>
              ) : null}
            </MDBox>

            {edicaoBloqueada ? (
              <Alert
                severity="info"
                sx={{ borderRadius: "12px", alignItems: "center" }}
                action={
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={handleBaixarPdf}
                    disabled={downloading || sending || valorCalculado <= 0}
                    startIcon={<Icon>picture_as_pdf</Icon>}
                    sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    {downloading ? "Gerando…" : "Baixar PDF"}
                  </MDButton>
                }
              >
                Proposta aprovada — os campos de edição foram bloqueados. Você
                ainda pode consultar os dados e baixar o PDF da proposta.
              </Alert>
            ) : null}

            {/* Cabeçalho / resumo */}
            <MDBox sx={sectionCardSx}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} lg={4}>
                  <MDTypography variant="overline" color="text.secondary">
                    Protocolo
                  </MDTypography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    mt={0.5}
                  >
                    <MDTypography variant="h5" fontWeight="bold">
                      {solicitacao.codigo}
                    </MDTypography>
                    <Chip
                      size="small"
                      label={STATUS_SOLICITACAO_LABEL[solicitacao.status]}
                      color={statusColor(solicitacao.status) as "default"}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        ORIGEM_SOLICITACAO_LABEL[solicitacao.origem ?? "SITE"]
                      }
                      color={
                        solicitacao.origem === "INTERNO" ? "primary" : "default"
                      }
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6} lg={4}>
                  <MDTypography variant="overline" color="text.secondary">
                    Recebida em
                  </MDTypography>
                  <MDTypography variant="h6" mt={0.5}>
                    {formatDateEndOfDay(solicitacao.createAt ?? "")}
                  </MDTypography>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <MDBox
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderRadius: "12px",
                      bgcolor: colors.grey[50],
                      border: `1px solid ${colors.grey[200]}`,
                      textAlign: { xs: "left", lg: "right" },
                    }}
                  >
                    <MDTypography variant="overline" color="text.secondary">
                      Subtotal atual
                    </MDTypography>
                    <MDTypography
                      variant="h4"
                      color="success.main"
                      fontWeight="bold"
                    >
                      {formatMoneyBR(valorCalculado)}
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </MDBox>

            {/* Contato + Evento */}
            <MDBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }}
              gap={2.5}
              width="100%"
            >
              <MDBox sx={{ ...sectionCardSx, height: "100%" }}>
                <MDTypography variant="h6" mb={2}>
                  Contato
                </MDTypography>
                <Stack spacing={1}>
                  <MDTypography variant="body1" fontWeight="medium">
                    {solicitacao.nomeOrganizador}
                  </MDTypography>
                  <MDTypography variant="body2">
                    {solicitacao.email}
                  </MDTypography>
                  <MDTypography variant="body2">
                    {solicitacao.telefone}
                  </MDTypography>
                </Stack>
              </MDBox>
              <MDBox sx={{ ...sectionCardSx, height: "100%" }}>
                <MDTypography variant="h6" mb={2}>
                  Evento
                </MDTypography>
                <Stack spacing={1}>
                  <MDTypography variant="body2">
                    <strong>Cidade:</strong> {solicitacao.cidade}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Data/hora:</strong>{" "}
                    {formatDateEndOfDay(solicitacao.dataEvento)} às{" "}
                    {solicitacao.horaLargada}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Modalidade:</strong> {solicitacao.modalidadeTexto}
                  </MDTypography>
                  <MDTypography variant="body2">
                    <strong>Distância:</strong> {solicitacao.distanciaProva} ·{" "}
                    <strong>Atletas:</strong> {solicitacao.numeroAtletas}
                  </MDTypography>
                  <MDTypography variant="body2" color="text.secondary">
                    Largada e chegada no mesmo ponto:{" "}
                    {solicitacao.largadaChegadaMesmoPonto ? "Sim" : "Não"}
                  </MDTypography>
                  {solicitacao.observacoes ? (
                    <MDBox
                      sx={{
                        mt: 1,
                        p: 1.5,
                        borderRadius: "8px",
                        bgcolor: colors.grey[50],
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Observações do cliente
                      </MDTypography>
                      <MDTypography variant="body2">
                        {solicitacao.observacoes}
                      </MDTypography>
                    </MDBox>
                  ) : null}
                </Stack>
              </MDBox>
            </MDBox>

            {/* Itens e precificação */}
            <MDBox sx={sectionCardSx}>
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1}
                mb={2}
              >
                <MDBox>
                  <MDTypography variant="h6">Itens e precificação</MDTypography>
                  <MDTypography variant="caption" color="text.secondary">
                    Altere quantidade e valor unitário, adicione ou remova
                    serviços. O total é calculado automaticamente — clique em
                    Salvar itens para persistir.
                  </MDTypography>
                </MDBox>
                <MDTypography variant="body2" color="text.secondary">
                  {itensEdit.length}{" "}
                  {itensEdit.length === 1 ? "serviço" : "serviços"}
                </MDTypography>
              </MDBox>

              <MDBox mb={2}>
                <Autocomplete
                  value={servicoParaAdicionar}
                  options={servicosDisponiveis}
                  disabled={edicaoBloqueada}
                  getOptionLabel={(option) => option.nome}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(_event, option) => handleAdicionarServico(option)}
                  noOptionsText="Todos os serviços já foram adicionados"
                  renderInput={(params) => (
                    <FormField
                      {...params}
                      label="Adicionar serviço ao orçamento"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      placeholder="Selecione um serviço do catálogo"
                    />
                  )}
                />
              </MDBox>

              <Stack spacing={2}>
                {itensEdit.map((item) => (
                  <Box
                    key={item.lineKey}
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      border: `1px solid ${colors.grey[200]}`,
                      borderRadius: "12px",
                      bgcolor: colors.grey[50],
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={3}>
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Tooltip title="Remover serviço">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoverItem(item.lineKey)}
                                disabled={edicaoBloqueada || itensEdit.length <= 1}
                                sx={{ mt: 0.5 }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              flexWrap="wrap"
                            >
                              <Typography variant="subtitle1" fontWeight="bold">
                                {item.nome}
                              </Typography>
                              {item.id <= 0 ? (
                                <Chip label="Novo" size="small" color="info" />
                              ) : null}
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Catálogo:{" "}
                              {formatMoneyBR(item.precoUnitarioCatalogo)} /{" "}
                              {item.unidadeMedida}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                      <Grid item xs={4} md={2}>
                        <FormField
                          label="Qtd"
                          type="number"
                          inputProps={{ min: 1, step: 1 }}
                          value={
                            itemInputs[item.lineKey]?.qtd ??
                            String(item.quantidade)
                          }
                          disabled={edicaoBloqueada || !item.permiteQuantidade}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleQuantidadeInputChange(
                              item.lineKey,
                              e.target.value
                            )
                          }
                          onBlur={() => handleQuantidadeBlur(item.lineKey)}
                        />
                      </Grid>
                      <Grid item xs={4} md={3}>
                        <FormField
                          label={`V. unit. (${item.unidadeMedida})`}
                          type="text"
                          inputProps={{ inputMode: "numeric" }}
                          disabled={edicaoBloqueada}
                          value={getUnitDisplayValue(
                            itemInputs[item.lineKey]?.unit,
                            item.valorUnitario
                          )}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleValorUnitarioInputChange(
                              item.lineKey,
                              e.target.value
                            )
                          }
                          onBlur={() => handleValorUnitarioBlur(item.lineKey)}
                        />
                      </Grid>
                      <Grid item xs={4} md={3}>
                        <FormField
                          label="Total"
                          value={formatMoneyBR(item.valorTotal)}
                          InputProps={{ readOnly: true }}
                          sx={{
                            "& .MuiInputBase-input": {
                              fontWeight: 700,
                              color: "success.main",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Stack>

              <MDBox
                mt={3}
                pt={2}
                borderTop={`1px solid ${colors.grey[200]}`}
                display="flex"
                gap={1.5}
                flexWrap="wrap"
                alignItems="center"
                justifyContent="space-between"
              >
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={() => handleSalvarItens(true)}
                    disabled={edicaoBloqueada || saving}
                  >
                    Atualizar do catálogo
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={() => handleSalvarItens(false)}
                    disabled={edicaoBloqueada || saving}
                  >
                    Salvar itens
                  </MDButton>
                </MDBox>
                <MDTypography variant="h5" fontWeight="bold">
                  Subtotal: {formatMoneyBR(valorCalculado)}
                </MDTypography>
              </MDBox>
            </MDBox>

            {/* Gestão interna + proposta */}
            <MDBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", lg: "1fr 1fr" }}
              gap={2.5}
              width="100%"
            >
              <MDBox sx={{ ...sectionCardSx, height: "100%" }}>
                <MDTypography variant="h6" mb={2}>
                  Gestão interna
                </MDTypography>
                <Stack spacing={2}>
                  <Autocomplete
                    value={
                      STATUS_AUTOCOMPLETE_OPTIONS.find(
                        (opt) => opt.value === status
                      ) ?? null
                    }
                    options={STATUS_AUTOCOMPLETE_OPTIONS}
                    disabled={edicaoBloqueada}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_event, option) => {
                      if (option) setStatus(option.value);
                    }}
                    renderInput={(params) => (
                      <FormField
                        {...params}
                        label="Status"
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />

                  <FormField
                    label="Observações internas"
                    multiline
                    minRows={5}
                    value={obsInterna}
                    disabled={edicaoBloqueada}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setObsInterna(e.target.value)
                    }
                  />

                  <MDButton
                    variant="outlined"
                    color="dark"
                    onClick={handleSalvarObsStatus}
                    disabled={edicaoBloqueada || saving}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Salvar status / obs. interna
                  </MDButton>
                </Stack>
              </MDBox>

              <MDBox sx={{ ...sectionCardSx, height: "100%" }}>
                <MDTypography variant="h6" mb={2}>
                  Proposta (PDF / e-mail)
                </MDTypography>
                <Stack spacing={2}>
                  <Box>
                    <FormField
                      label="Valor proposto (opcional — usa subtotal se vazio)"
                      type="text"
                      inputProps={{ inputMode: "numeric" }}
                      disabled={edicaoBloqueada}
                      value={getValorPropostoDisplayValue(valorPropostoManual)}
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleValorPropostoInputChange(e.target.value)
                      }
                      placeholder={formatMoneyBR(valorCalculado)}
                    />
                    {ajusteProposta ? (
                      <MDTypography
                        variant="caption"
                        color={
                          ajusteProposta.tipo === "desconto"
                            ? "success"
                            : "warning.main"
                        }
                        sx={{ mt: 0.75, display: "block", fontWeight: 500 }}
                      >
                        {ajusteProposta.tipo === "desconto"
                          ? `Desconto de ${formatMoneyBR(ajusteProposta.valor)} (${ajusteProposta.percentual.toFixed(1)}%) em relação ao subtotal de ${formatMoneyBR(valorCalculado)}.`
                          : `Acréscimo de ${formatMoneyBR(ajusteProposta.valor)} (${ajusteProposta.percentual.toFixed(1)}%) em relação ao subtotal de ${formatMoneyBR(valorCalculado)}.`}
                      </MDTypography>
                    ) : null}
                  </Box>
                  <FormField
                    label="Mensagem no e-mail (opcional)"
                    multiline
                    minRows={5}
                    value={mensagemEmail}
                    disabled={edicaoBloqueada}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setMensagemEmail(e.target.value)
                    }
                  />
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{ alignSelf: { xs: "stretch", sm: "flex-start" } }}
                  >
                    {!edicaoBloqueada ? (
                      <MDButton
                        variant="outlined"
                        color="dark"
                        onClick={handleBaixarPdf}
                        disabled={downloading || sending || valorCalculado <= 0}
                        startIcon={<Icon>picture_as_pdf</Icon>}
                      >
                        {downloading ? "Gerando…" : "Baixar PDF"}
                      </MDButton>
                    ) : null}
                    <MDButton
                      variant="gradient"
                      color="success"
                      onClick={handleEnviarProposta}
                      disabled={
                        edicaoBloqueada || sending || downloading || valorCalculado <= 0
                      }
                      startIcon={<Icon>send</Icon>}
                    >
                      {sending ? "Enviando…" : "Enviar proposta (PDF + e-mail)"}
                    </MDButton>
                  </Stack>
                </Stack>
              </MDBox>
            </MDBox>

            {solicitacao.historico && solicitacao.historico.length > 0 ? (
              <MDBox sx={sectionCardSx}>
                <MDTypography variant="h6" mb={2}>
                  Histórico
                </MDTypography>
                <Stack spacing={1.5} divider={<Divider flexItem />}>
                  {solicitacao.historico.map((h) => (
                    <Box key={h.id}>
                      <MDTypography variant="body2" fontWeight="medium">
                        {formatDateEndOfDay(h.createAt)} —{" "}
                        {STATUS_SOLICITACAO_LABEL[h.statusNovo]}
                      </MDTypography>
                      {h.observacao ? (
                        <MDTypography variant="body2" color="text.secondary">
                          {h.observacao}
                        </MDTypography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </MDBox>
            ) : null}
          </MDBox>
        )}
      </MDBox>

      <ConfirmDialog
        open={confirmDeleteOpen}
        title="Excluir solicitação"
        message={`Excluir a solicitação ${solicitacao?.codigo ?? ""}? Esta ação não pode ser desfeita.`}
        confirmLabel={deleting ? "Excluindo…" : "Excluir"}
        onClose={() => {
          if (!deleting) setConfirmDeleteOpen(false);
        }}
        onConfirm={() => {
          void handleExcluir();
        }}
      />
    </FullScreenDialog>
  );
}
